import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Grid,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import axios from '../../api/client';
import { useCurrency } from '../../context/CurrencyContext';
import { toast } from 'react-toastify';
import { format } from 'date-fns';


const PaymentSubmission = () => {
  const [payments, setPayments] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const { formatMoney: formatCurrency } = useCurrency();
  const [paymentModes, setPaymentModes] = useState([]);
  const [formData, setFormData] = useState({
    amountPaid: '',
    periodStart: '',
    periodEnd: '',
    paymentModeId: '',
    paymentDate: new Date().toISOString().split('T')[0],
    tenantResponse: '',
  });

  useEffect(() => {
    fetchPayments();
    fetchPaymentModes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tenants say how they paid. The account it lands in is set by staff at review — the
  // API strips it from this response, so it is never exposed here.
  const fetchPaymentModes = async () => {
    try {
      const response = await axios.get('/payment-modes?isActive=true');
      setPaymentModes(response.data || []);
    } catch (error) {
      setPaymentModes([]);
    }
  };


  const fetchPayments = async () => {
    try {
      const response = await axios.get('/payments');
      setPayments(Array.isArray(response.data) ? response.data : (response.data.data || []));
    } catch (error) {
      toast.error('Error fetching payments');
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleOpen = (payment = null) => {
    if (payment && payment.status === 'queried') {
      setEditing(payment._id);
      setFormData({
        amountPaid: payment.amountPaid,
        periodStart: payment.periodStart || '',
        periodEnd: payment.periodEnd || '',
        paymentModeId: payment.paymentMode?._id || payment.paymentModeId || '',
        paymentDate: new Date(payment.paymentDate).toISOString().split('T')[0],
        tenantResponse: payment.tenantResponse || '',
      });
    } else {
      setEditing(null);
      setFormData({
        amountPaid: '',
        periodStart: '',
        periodEnd: '',
        paymentModeId: '',
        paymentDate: new Date().toISOString().split('T')[0],
        tenantResponse: '',
      });
    }
    setFile(null);
    setOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.amountPaid || !formData.paymentDate) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!formData.periodStart || !formData.periodEnd) {
      toast.error('Please give the period this payment covers, from and to');
      return;
    }
    if (formData.periodEnd < formData.periodStart) {
      toast.error('Period end cannot be before period start');
      return;
    }

    if (!file && !editing) {
      toast.error('Please upload a receipt');
      return;
    }

    setUploading(true);

    try {
      let receiptFile = null;

      // Upload file if provided
      if (file) {
        const formDataUpload = new FormData();
        formDataUpload.append('receipt', file);

        const uploadResponse = await axios.post(
          '/uploads/payment-receipt',
          formDataUpload,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
        receiptFile = uploadResponse.data.receiptFile;
      }

      // Flatten to the Payment model's actual columns (receiptFileName/
      // receiptFilePath/receiptFileUploadedAt) — the upload endpoint returns
      // a nested object, but there's no matching nested column to write it to.
      const receiptFields = receiptFile
        ? {
            receiptFileName: receiptFile.fileName,
            receiptFilePath: receiptFile.filePath,
            receiptFileUploadedAt: receiptFile.uploadedAt,
          }
        : {};

      if (editing) {
        // Update existing payment
        await axios.put(`/payments/${editing}`, {
          ...formData,
          ...receiptFields,
        });
        toast.success('Payment updated successfully');
      } else {
        // Create new payment
        await axios.post('/payments', {
          ...formData,
          ...receiptFields,
        });
        toast.success('Payment submitted successfully');
      }

      setOpen(false);
      setFile(null);
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error submitting payment');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadReceipt = async (payment) => {
    try {
      const url = `/payments/${payment._id}/receipt`;
      const res = await axios.get(url, {
        responseType: 'blob'
      });
      
      // Check if response is actually a PDF
      if (res.data.type && res.data.type !== 'application/pdf') {
        const text = await res.data.text();
        try {
          const error = JSON.parse(text);
          toast.error(error.message || 'Unable to download receipt PDF');
        } catch {
          toast.error('Server returned non-PDF response');
        }
        return;
      }

      const file = new Blob([res.data], { type: 'application/pdf' });
      const url2 = window.URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url2;
      link.download = `receipt-${payment.receiptNumber || payment._id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url2);
    } catch (e) {
      let errorMessage = 'Unable to download receipt PDF';
      if (e.response) {
        if (e.response.data instanceof Blob) {
          try {
            const text = await e.response.data.text();
            const error = JSON.parse(text);
            errorMessage = error.message || errorMessage;
          } catch {
            errorMessage = `Error ${e.response.status}: ${e.response.statusText}`;
          }
        } else {
          errorMessage = e.response.data?.message || errorMessage;
        }
      } else if (e.message) {
        errorMessage = e.message;
      }
      toast.error(errorMessage);
      console.error('Receipt download error:', e);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      received: 'success',
      queried: 'info',
      rejected: 'error',
    };
    return colors[status] || 'default';
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Payment Submissions</Typography>
        <Button
          variant="contained"
          startIcon={<CloudUploadIcon />}
          onClick={() => handleOpen()}
        >
          Submit Payment
        </Button>
      </Box>

      {payments.length > 0 && (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Payment Date</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Rent Period</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Receipt</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((payment) => (
                <TableRow key={payment._id}>
                  <TableCell>
                    {format(new Date(payment.paymentDate), 'MMM dd, yyyy')}
                  </TableCell>
                  <TableCell>{formatCurrency(payment.amountPaid)}</TableCell>
                  <TableCell>
                    {payment.periodStart && payment.periodEnd
                      ? `${format(new Date(payment.periodStart), 'dd MMM yy')} – ${format(new Date(payment.periodEnd), 'dd MMM yy')}`
                      : (payment.numberOfMonths ? `${payment.numberOfMonths} month(s)` : '-')}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={payment.status}
                      color={getStatusColor(payment.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {payment.receiptNumber ? (
                      <Button
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownloadReceipt(payment)}
                      >
                        {payment.receiptNumber}
                      </Button>
                    ) : (
                      'Pending'
                    )}
                  </TableCell>
                  <TableCell>
                    {payment.status === 'queried' && (
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleOpen(payment)}
                      >
                        Edit
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editing ? 'Edit Payment Submission' : 'Submit Payment'}
        </DialogTitle>
        <DialogContent>
          {editing && payments.find(p => p._id === editing)?.staffComments && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Staff Comments:</Typography>
              <Typography>{payments.find(p => p._id === editing)?.staffComments}</Typography>
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Amount Paid"
                type="number"
                value={formData.amountPaid}
                onChange={(e) =>
                  setFormData({ ...formData, amountPaid: parseFloat(e.target.value) || 0 })
                }
              />
            </Grid>
            {/* Which months this payment is for. Paying several months up front is just a
                longer period, which records exactly what was bought. */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                required
                type="date"
                label="Period From"
                InputLabelProps={{ shrink: true }}
                value={formData.periodStart}
                onChange={(e) => setFormData({ ...formData, periodStart: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                required
                type="date"
                label="Period To"
                InputLabelProps={{ shrink: true }}
                value={formData.periodEnd}
                onChange={(e) => setFormData({ ...formData, periodEnd: e.target.value })}
                inputProps={{ min: formData.periodStart || undefined }}
                error={!!formData.periodEnd && !!formData.periodStart && formData.periodEnd < formData.periodStart}
                helperText="Extend for an advance payment"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                required
                label="How did you pay?"
                value={formData.paymentModeId}
                onChange={(e) => setFormData({ ...formData, paymentModeId: e.target.value })}
                SelectProps={{ native: false }}
                helperText={paymentModes.length === 0 ? 'Contact the office' : 'Mode of payment'}
              >
                {paymentModes.map((mode) => (
                  <MenuItem key={mode._id} value={mode._id}>{mode.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Payment Date"
                type="date"
                value={formData.paymentDate}
                onChange={(e) =>
                  setFormData({ ...formData, paymentDate: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            {editing && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Your Response"
                  multiline
                  rows={3}
                  value={formData.tenantResponse}
                  onChange={(e) =>
                    setFormData({ ...formData, tenantResponse: e.target.value })
                  }
                  placeholder="Please provide clarification or corrections..."
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <input
                accept="image/*,.pdf"
                style={{ display: 'none' }}
                id="receipt-upload"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="receipt-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  fullWidth
                >
                  {file ? `Selected: ${file.name}` : 'Upload Receipt'}
                </Button>
              </label>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={uploading}
          >
            {uploading ? 'Submitting...' : editing ? 'Update' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default PaymentSubmission;


